import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonService } from 'src/app/shared/services/common.service';
import { SitesService } from 'src/app/sites/sites.service';
import * as moment from 'moment';
import { LegendGroup, LegendItem } from '../widgets/utilization-metric-box/utilization-metrics.interface';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ChargingType, SiteTemperatureUnit } from 'src/app/home/site-dashboard/site-settings/site-utilization-settings/site-utilization-settings.interface';

@Component({
	selector: 'app-daily-battlink-usage-metrics',
	templateUrl: './daily-battlink-usage-metrics.component.html',
	styleUrls: ['./daily-battlink-usage-metrics.component.css']
})
export class DailyBattlinkUsageMetricsComponent implements OnChanges {

	@Input() devices: any = {};
	siteID: number = 0;

	@Input() userParentFilters: boolean = false;

	@Input() toDate = new Date();
	@Input() fromDate = new Date(this.toDate.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	siteInstallationDate: Date = new Date(0);

	batteryVoltagesOptions: any[] = [];
	@Input() selectedBatteryVoltage: string = 'all';
	batteryCapacitiesOptions: any[] = [];
	@Input() selectedBatteryCapacity: string = 'all';
	manufacturersOptions: any[] = [];
	@Input() selectedManufacturer: string = 'all';

	@Input() selectedBatteryVintage: any[] = ['all'];

	@Output() prepareParentFiltersOptions = new EventEmitter<{ batteryVoltagesOptions: any[], batteryCapacitiesOptions: any[], manufacturers: string[] }>();

	showFastOpportunityWidget: boolean = false;
	charging: any = [];
	maintenance: any = [];
	assetUtilization: any = [];

	noData: boolean = false;

	batteryDailyAvgUsageLegends: LegendGroup[] = [
		{
			title: this.translateService.instant("utilization_metrics.hour_usage"),
			items: [
				{ label: 'In-Use', type: 'bar', colorClass: 'in-use' },
				{ label: 'Charge', type: 'bar', colorClass: 'charge' },
				{ label: 'Idle', type: 'bar', colorClass: 'idle' }
			]
		},
		{
			title: this.translateService.instant("utilization_metrics.amp_hours"),
			items: [
				{ label: 'In-Use Average', type: 'bar', colorClass: 'ah-inuse' },
				{ label: 'Charge Average (105–115%)', type: 'bar', colorClass: 'ah-charge' },
				{ label: 'Ahrs Return (>115%)', type: 'bar', colorClass: 'ah-return' }
			]
		},
		{
			title: this.translateService.instant("utilization_metrics.exception_severity"),
			items: [
				{ label: 'Low (<5%)', severity: 'Low' },
				{ label: 'Moderate (5–10%)', severity: 'Moderate' },
				{ label: 'High (10–30%)', severity: 'High' },
				{ label: 'Critical (>30%)', severity: 'Critical' }
			]
		}
	];

	batteryUtilizationSiteSettings: any = {};

	constructor(
		private siteService: SitesService,
		private commonService: CommonService,
		private route: ActivatedRoute,
		private translateService: TranslateService
	) {}

	ngOnInit() {
	}

	changeDate(field, value) {
		this[field] = value;
		this.getDailyUtilizationData();
	}

	getDailyUtilizationData() {
		let zoneDiff = new Date().getTimezoneOffset() * -1;
		let fromDate: any = new Date(new Date(this.fromDate).getTime() + (zoneDiff * 60 * 1000));
		let toDate: any = new Date(new Date(this.toDate).getTime() + (zoneDiff * 60 * 1000));
		
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();
		
		if (!this.siteID)
			return this.noData = true;

		this.siteService.getSiteUtilizationSettings(this.siteID).subscribe((res: any) => {
			const batteryUtilizationSiteSettings = res[0].batteries_utilization_settings;
			this.batteryUtilizationSiteSettings = batteryUtilizationSiteSettings;

			this.showFastOpportunityWidget = batteryUtilizationSiteSettings?.charging_type == ChargingType.OPPORTUNITY_FAST;

			const options = {
				batteryCapacity: this.selectedBatteryCapacity,
				batteryVoltage: this.selectedBatteryVoltage,
				manufacturer: this.selectedManufacturer,
				batteryVintage: this.selectedBatteryVintage,
			};

			this.siteService.getDailyAvgBatteriesUsageReport(fromDate, toDate, this.siteID, options).subscribe((res: any) => {
				this.noData = !res || !res?.reportData || Object.keys(res.reportData).length === 0;
				this.handleDailyUtilizationData(res);
			});
		});
	}

	prepareFilters(deviceFilters) {
		this.batteryVoltagesOptions = this.commonService.prepareOptions(deviceFilters.batteryVoltages);
		this.batteryCapacitiesOptions = this.commonService.prepareOptions(deviceFilters.batteryCapacities);
		this.manufacturersOptions = this.commonService.prepareOptions(deviceFilters.manufacturers);

		this.prepareParentFiltersOptions.emit({ batteryVoltagesOptions: this.batteryVoltagesOptions, batteryCapacitiesOptions: this.batteryCapacitiesOptions, manufacturers: this.manufacturersOptions });
	}

	ngOnChanges() {
		if (Object.keys(this.devices).length) {
			this.siteID = this.route.parent.snapshot.params.siteId || 0;
			this.getDailyUtilizationData();
		} else {
			return this.noData = true;
		}
	}

	onSelectionChanged(event) {
		switch (event.filter) {
			case 'voltageFilter':
				this.selectedBatteryVoltage = event.value;
				break;
			case 'capacityFilter':
				this.selectedBatteryCapacity = event.value;
				break;
			default:
				break;
		}

		this.getDailyUtilizationData();
	}

	handleDailyUtilizationData(res) {
		const dailyAhrUsageData = res.reportData;
		const totalBatteryCapacity: number = res.totalBatteryCapacity || 0;
		this.prepareFilters(res);

		/* ---------------- HOURS ---------------- */
		const inUseHrs = dailyAhrUsageData.avg_inuse_seconds / 3600;
		const chargeHrs = dailyAhrUsageData.avg_charge_seconds / 3600;
		const idleHrs = dailyAhrUsageData.avg_idle_seconds / 3600;

		this.assetUtilization = [
			{
				label: this.translateService.instant("utilization_metrics.in_use_hours"),
				value: Math.round((inUseHrs / 24) * 100),
				displayValue: `${inUseHrs?.toFixed(1)}h`,
				percentage: Math.round((inUseHrs / 24) * 100)?.toFixed(1),
				showPercentage: true,
				colorClass: "inuase_hours_avg"
			},
			{
				label: this.translateService.instant("utilization_metrics.charge_hours"),
				value: Math.round((chargeHrs / 24) * 100),
				displayValue: `${chargeHrs?.toFixed(1)}h`,
				percentage: Math.round((chargeHrs / 24) * 100)?.toFixed(1),
				showPercentage: true,
				colorClass: 'charge_hours'
			},
			{
				label: this.translateService.instant("utilization_metrics.idle_hours"),
				value: Math.round((idleHrs / 24) * 100),
				displayValue: `${idleHrs?.toFixed(1)}h`,
				percentage: Math.round((idleHrs / 24) * 100)?.toFixed(1),
				showPercentage: true,
				colorClass: 'idle_hours'
			}
		];

		/* ---------------- AHRS ---------------- */
		const inUseAhrs = dailyAhrUsageData.avg_inuse_ahrs || 0;
		const chargeAhrs = dailyAhrUsageData.avg_charge_ahrs || 0;
		const totalDailyAvailable = 0.8 * totalBatteryCapacity;
		const ahrReturn = Math.round(dailyAhrUsageData.avg_battery_ah_return_score || 0);

		this.maintenance = [
			{
				label: this.translateService.instant("utilization_metrics.in_use_average_ahrs"),
				value: Math.round(dailyAhrUsageData.avg_energy_usage_score || 0),
				displayValue: `${this.commonService.formatNumber(inUseAhrs)} Ah`,
				showPercentage: true,
				percentage: inUseAhrs ? (((inUseAhrs *100 ) / totalDailyAvailable ))?.toFixed(1) : 0?.toFixed(1),
				colorClass: 'inuase_hours_avg'
			},
			{
				label: this.translateService.instant("utilization_metrics.charge_ahrs"),
				value: Math.round(dailyAhrUsageData.avg_charge_ahrs),
				displayValue: `${this.commonService.formatNumber(chargeAhrs)} Ah`,
				showPercentage: true,
				percentage: chargeAhrs ? ((chargeAhrs*100) / totalDailyAvailable)?.toFixed(1) : 0?.toFixed(1),
				colorClass: 'charge_hours'
			},
			{
				label: this.translateService.instant("utilization_metrics.idle_ahrs"),
				value: ahrReturn,
				showPercentage: false,
				displayValue: `${ahrReturn > 150 ? 150 : ahrReturn}%`,
				colorClass: 'ahr_return',
			}
		];

		/* ---------------- OPPORTUNITY / FAST ---------------- */
		this.charging = [
			{
				label: this.prepareMetricFieldName('batteries_with_daily_not_plug_ins'),
				value: dailyAhrUsageData.not_plugged_in_devices_percent,
				percentage: dailyAhrUsageData.not_plugged_in_devices_percent,
				showPercentage: true,
			},
			{
				label: this.prepareMetricFieldName('batteries_with_low_soc'),
				value: dailyAhrUsageData.low_soc_devices_percent,
				percentage: dailyAhrUsageData.low_soc_devices_percent,
				showPercentage: true,
			},
			{
				label: this.prepareMetricFieldName('high_temperature_batteries'),
				value: dailyAhrUsageData.high_temperature_devices_percent,
				percentage: dailyAhrUsageData.high_temperature_devices_percent,
				showPercentage: true,
			},
		];
	}

	prepareMetricFieldName(field: string): string {
		let name = '';

		switch (field) {
			case 'batteries_with_daily_not_plug_ins':
				name = `${this.translateService.instant(`utilization_metrics.${field}`)} (< ${this.batteryUtilizationSiteSettings.min_daily_plug_ins})`;
				break;
			case 'batteries_with_low_soc':
				name = `${this.translateService.instant(`utilization_metrics.${field}`)} (< ${this.batteryUtilizationSiteSettings.min_daily_battery_soc}%)`;
				break;
			case 'high_temperature_batteries':
				name = `${this.translateService.instant(`utilization_metrics.${field}`)} (> ${this.getTemp(this.batteryUtilizationSiteSettings.temperature_unit)})`;
				break;
			default:
				break;
		}

		return name;
	}

	getTemp(unit: number) {
		let temp;
		if (unit == SiteTemperatureUnit.CELSIUS) {
			temp = this.commonService.fahToCel(this.batteryUtilizationSiteSettings.high_temperature_limit);
			return `${temp}°C`;
		}
		else {
			temp = this.batteryUtilizationSiteSettings.high_temperature_limit;
			return `${temp}°F`;
		}
	}
}
